import { Settings, Wrench } from "lucide-react";
import { UserButton, useUser } from "@clerk/react";
import brandIcon from "../../assets/brand-icon.png";
import { useDbHealth } from "../../hooks/useApi";

export function Header({
  onOpenSettings,
  onOpenDevTools,
}: {
  onOpenSettings: () => void;
  onOpenDevTools: () => void;
}) {
  const { user } = useUser();
  // Cosmetic gate only — the /api/dev endpoints 403 anyone but the owner.
  // Viktor signs in as viktor@viktor.is on dev and via Google on prod.
  const OWNER_EMAILS = ["viktor@viktor.is", "viktor.kjartansson@gmail.com"];
  const isOwner = OWNER_EMAILS.includes(user?.primaryEmailAddress?.emailAddress ?? "");
  // Green/red database dot; a failed request (server down) also reads as red.
  const dbHealth = useDbHealth();
  const dbState = dbHealth.isPending ? "checking" : dbHealth.data?.ok ? "up" : "down";
  return (
    <header className="relative flex h-14 shrink-0 items-center bg-[#17202a] px-4 text-white shadow-[0_1px_8px_rgb(23_32_42/18%)]">
      <div className="absolute inset-x-0 bottom-0 h-px bg-[#3478d4]" aria-hidden="true" />

      <a href="/" className="group flex items-center gap-2.5" aria-label="mysmall.life home">
        <span className="grid size-8 place-items-center overflow-hidden rounded-md bg-white ring-1 ring-white/20 transition-transform group-hover:scale-[1.04]">
          <img src={brandIcon} alt="" className="size-full object-cover" />
        </span>
        <h1 className="text-[16px] font-semibold text-white">mysmall.life</h1>
      </a>

      <div className="mx-3.5 h-5 w-px bg-white/18" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-[12px] text-[#b9c5d1] max-sm:hidden">
        your email and calendar, in one calm place
      </span>

      <div className="flex items-center gap-2">
        <span
          title={
            dbState === "up"
              ? "Database connected"
              : dbState === "down"
                ? "Database unreachable"
                : "Checking database…"
          }
          className={`mr-1 size-2.5 rounded-full ${
            dbState === "up"
              ? "bg-emerald-400"
              : dbState === "down"
                ? "bg-red-500"
                : "bg-white/30"
          }`}
        />
        {isOwner && (
          <button
            onClick={onOpenDevTools}
            title="Dev tools"
            aria-label="Dev tools"
            className="grid size-9 place-items-center rounded-md border border-white/10 bg-white/[0.04] text-[#c9d3dc] transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white"
          >
            <Wrench size={17} strokeWidth={1.9} />
          </button>
        )}
        <button
          onClick={onOpenSettings}
          title="Settings"
          aria-label="Settings"
          className="grid size-9 place-items-center rounded-md border border-white/10 bg-white/[0.04] text-[#c9d3dc] transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white"
        >
          <Settings size={17} strokeWidth={1.9} />
        </button>
        <span className="grid size-9 place-items-center rounded-md border border-white/10 bg-white/[0.04]">
          <UserButton
            appearance={{
              elements: {
                avatarBox: { width: "28px", height: "28px" },
                userButtonTrigger: { outline: "none" },
              },
            }}
          />
        </span>
      </div>
    </header>
  );
}
